import { type Signal, computed, inject } from '@angular/core';
import { WEBRTC_SIGNALING_URL } from '@animus/utility/web/persistence';
import {
    type EmptyFeatureResult,
    type SignalStoreFeature,
    patchState,
    signalStoreFeature,
    withComputed,
    withMethods,
    withProps,
    withState,
} from '@ngrx/signals';
import { type Observable, type Subscription, firstValueFrom, map, timer } from 'rxjs';
import type { AnimusDatabase } from './collections';
import { RxDatabaseService } from './rxdb.service';

/** Which side of a peer-to-peer replication this tab is on for the active entity. */
export type ReplicationRole = 'host' | 'joiner' | 'none';

/** Connection lifecycle of the active pool, from idle through bounded retries to a terminal failure. */
export type ReplicationStatus = 'offline' | 'connecting' | 'live' | 'degraded' | 'retrying' | 'failed';

/**
 * The minimal surface `withReplicationPool` needs from an RxDB `RxWebRTCReplicationPool` (or a composite of
 * them), decoupling the feature from the concrete rxdb type (which lives behind a lazy import so the WebRTC
 * stack stays out of the initial bundle). `subs` is the pool's own array that `cancel()` drains - pushing our
 * status-tracking subscriptions onto it ties their teardown to the pool's cancellation, so they can never leak
 * across reconnects.
 */
export interface ReplicationPool {
    cancel(): Promise<void>;
    peerStates$: Observable<{ readonly size: number }>;
    error$: Observable<unknown>;
    subs: Subscription[];
}

export interface ReplicationPoolConfig<TRequest> {
    /** Short label used only in error logs, e.g. `'game'` or `'horde'`. */
    label: string;
    /** Total connection attempts for one request before reporting a terminal `failed` status. Defaults to 4. */
    maxAttempts?: number;
    /**
     * Lazily start a pool for `request`. The implementation owns the dynamic `import()` (keeping the ~96 kB
     * WebRTC stack out of the initial bundle) and the topic naming; the feature only orchestrates lifecycle.
     * The context carries a lazy `getDatabase` accessor (resolving {@link RxDatabaseService.database} at start
     * time, after the eager app initializer has created it) so the WebRTC factory can open pools against the
     * app's database.
     */
    start: (
        request: TRequest,
        context: {
            getDatabase: () => AnimusDatabase;
            role: Exclude<ReplicationRole, 'none'>;
            signalingUrl: string;
        }
    ) => Promise<ReplicationPool>;
}

interface ReplicationPoolState<TRequest> {
    activeRequest: TRequest | null;
    diagnostics: string | null;
    peerCount: number;
    retryAttempt: number;
    role: ReplicationRole;
    status: ReplicationStatus;
}

/** The state/props/methods {@link withReplicationPool} contributes - the feature's public shape. */
type ReplicationPoolResult<TRequest> = {
    state: ReplicationPoolState<TRequest>;
    props: {
        hostConnected: Signal<boolean>;
        isJoiner: Signal<boolean>;
        _generation: { current: number };
        _lastConnect: { current: { request: TRequest; role: Exclude<ReplicationRole, 'none'> } | null };
        _pool: { current: { key: string; pool: ReplicationPool } | null };
        _queue: { current: Promise<unknown> };
        _signalingUrl: string;
    };
    methods: {
        _connect(request: TRequest, role: Exclude<ReplicationRole, 'none'>): void;
        leave(): void;
        retry(): void;
    };
};

function errorMessage(error: unknown, fallback: string): string {
    return error instanceof Error ? error.message : fallback;
}

/**
 * Reusable SignalStore feature owning the single active WebRTC replication pool for one entity type - the one
 * wrapper around RxDB's `replicateWebRTC` lifecycle in the app. Every replication store (game, bracket, Horde)
 * builds on it, so queueing, retry, teardown, and status reporting exist exactly once.
 *
 * `_connect(request, role)` (re)starts replication for a request; stores wrap it in their public `host`/`join`
 * methods (an `rxMethod` wrapper lets callers feed an id signal). A repeat of the exact active request is a
 * no-op. The pool is deliberately NOT torn down when a calling component is destroyed - a peer keeps syncing
 * until this tab connects to a different request (which cancels the previous pool) or calls `leave()`.
 *
 * Pool transitions are serialized through `_queue`: starting a pool is async (dynamic import +
 * `waitForLeadership`), so without serialization two rapid calls could both pass the no-op guard before either
 * assigns `_pool`, leaking a pool nothing can cancel. `_generation` cancels stale queued work: `leave()` or a
 * newer `_connect` bumps it, and a queued retry loop observing a stale generation exits without touching state.
 * `replicateWebRTC` awaits `waitForLeadership()` internally, so a start only takes real effect in the leader
 * tab - callers must treat connecting as best-effort fire-and-forget and never await it in a UI flow.
 *
 * Failures retry with jittered exponential backoff up to `maxAttempts`, surfacing progress through `status`,
 * `retryAttempt`, and `diagnostics`; `retry()` re-runs the most recent request after a terminal failure.
 */
export function withReplicationPool<TRequest>(
    config: ReplicationPoolConfig<TRequest>
): SignalStoreFeature<EmptyFeatureResult, ReplicationPoolResult<TRequest>> {
    const maxAttempts = config.maxAttempts ?? 4;

    return signalStoreFeature(
        withState<ReplicationPoolState<TRequest>>({
            activeRequest: null,
            diagnostics: null,
            peerCount: 0,
            retryAttempt: 0,
            role: 'none',
            status: 'offline',
        }),
        withComputed((store) => ({
            hostConnected: computed(() => store.peerCount() > 0 || store.role() === 'host'),
            isJoiner: computed(() => store.role() === 'joiner'),
        })),
        withProps(() => ({
            _databaseService: inject(RxDatabaseService),
            // Monotonic token cancelling stale queued work (see the feature doc above).
            _generation: { current: 0 },
            // The most recent request, kept for `retry()` after a terminal failure.
            _lastConnect: {
                current: null as { request: TRequest; role: Exclude<ReplicationRole, 'none'> } | null,
            },
            // Non-reactive handle to the live pool (a WebRTC/RxDB object, not serializable store state).
            _pool: { current: null as { key: string; pool: ReplicationPool } | null },
            // Serializes pool transitions - see the feature doc above.
            _queue: { current: Promise.resolve<unknown>(undefined) },
            _signalingUrl: inject(WEBRTC_SIGNALING_URL),
        })),
        withMethods((store) => {
            /** Stable identity for the no-op guard: same role + structurally identical request. */
            function requestKey(request: TRequest, role: Exclude<ReplicationRole, 'none'>): string {
                return `${role}|${JSON.stringify(request)}`;
            }

            /** Cancels the currently active pool (its `subs` carry our status observers down with it). */
            async function cancelActive(): Promise<void> {
                const active = store._pool.current;

                if (!active) {
                    return;
                }

                store._pool.current = null;
                await active.pool.cancel();
            }

            /** Run `task` after the previously-queued transition settles (success or failure), never concurrently. */
            function enqueue(task: () => Promise<void>): void {
                const next = store._queue.current.then(task, task);

                store._queue.current = next.catch((error: unknown) => {
                    console.error(`[WebRTC][${config.label}]`, error);
                    patchState(store, {
                        diagnostics: errorMessage(error, 'Replication failed.'),
                        status: 'failed',
                    });
                });
            }

            /** Exponential backoff (500ms base, 8s cap) with ±25% jitter so peers do not retry in lockstep. */
            function retryDelay(attempt: number): number {
                const bounded = Math.min(500 * 2 ** Math.max(0, attempt - 1), 8_000);

                return Math.round(bounded * (0.75 + Math.random() * 0.5));
            }

            function delay(milliseconds: number): Promise<void> {
                return firstValueFrom(timer(milliseconds).pipe(map(() => undefined)));
            }

            /** Replaces the active pool with one for `request`. A no-op when that exact request is already live. */
            async function start(request: TRequest, role: Exclude<ReplicationRole, 'none'>): Promise<void> {
                const key = requestKey(request, role);

                if (store._pool.current?.key === key) {
                    return;
                }

                await cancelActive();
                patchState(store, {
                    activeRequest: request,
                    diagnostics: null,
                    peerCount: 0,
                    retryAttempt: 0,
                    role,
                    status: 'connecting',
                });

                const pool = await config.start(request, {
                    getDatabase: () => store._databaseService.database,
                    role,
                    signalingUrl: store._signalingUrl,
                });

                // Tie status tracking to the pool's own cancellation (see `ReplicationPool.subs`).
                pool.subs.push(
                    pool.peerStates$.subscribe((peers) => {
                        patchState(store, {
                            peerCount: peers.size,
                            status: peers.size > 0 || role === 'host' ? 'live' : 'connecting',
                        });
                    }),
                    pool.error$.subscribe((error) => {
                        patchState(store, {
                            diagnostics: errorMessage(error, 'Replication connection degraded.'),
                            status: 'degraded',
                        });
                    })
                );
                store._pool.current = { key, pool };
                patchState(store, { status: role === 'host' ? 'live' : 'connecting' });
            }

            /** Starts a pool with bounded retry while guarding delayed work against cancellation. */
            async function startWithRetry({
                generation,
                request,
                role,
            }: {
                generation: number;
                request: TRequest;
                role: Exclude<ReplicationRole, 'none'>;
            }): Promise<void> {
                for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
                    if (generation !== store._generation.current) {
                        return;
                    }

                    try {
                        await start(request, role);

                        return;
                    } catch (error: unknown) {
                        if (generation !== store._generation.current) {
                            return;
                        }

                        const diagnostics = errorMessage(error, 'Replication could not start.');

                        if (attempt === maxAttempts - 1) {
                            patchState(store, { diagnostics, retryAttempt: attempt, status: 'failed' });

                            return;
                        }

                        patchState(store, {
                            diagnostics,
                            retryAttempt: attempt + 1,
                            status: 'retrying',
                        });
                        await delay(retryDelay(attempt + 1));
                    }
                }
            }

            return {
                /** (Re)starts replication for `request` under `role` - wrapped by each store's public host/join. */
                _connect(request: TRequest, role: Exclude<ReplicationRole, 'none'>): void {
                    const generation = ++store._generation.current;

                    store._lastConnect.current = { request, role };
                    enqueue(() => startWithRetry({ generation, request, role }));
                },

                /** Stops replication and resets the connection state to offline. */
                leave(): void {
                    const generation = ++store._generation.current;

                    store._lastConnect.current = null;
                    enqueue(async () => {
                        if (generation !== store._generation.current) {
                            return;
                        }

                        await cancelActive();
                        patchState(store, {
                            activeRequest: null,
                            diagnostics: null,
                            peerCount: 0,
                            retryAttempt: 0,
                            role: 'none',
                            status: 'offline',
                        });
                    });
                },

                /** Retries the most recent request after a terminal/degraded failure. */
                retry(): void {
                    const last = store._lastConnect.current;

                    if (!last) {
                        return;
                    }

                    const generation = ++store._generation.current;

                    enqueue(() => startWithRetry({ generation, request: last.request, role: last.role }));
                },
            };
        })
    );
}
